import { defineComponent as _defineComponent } from 'vue'
import axios from 'axios'
import {
  getCurrentInstance,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref
} from 'vue'
import { useEventListener } from '@vueuse/core'
import { useAuth } from '@/composables/useAuth'
import { emitter } from '@/plugins/emitter'
import PaymentForm from '@/components/Payment/PaymentMethodSelector.vue'

type HostVm = {
  $deviceLib: { isMobileDevice: boolean }
  $site: { appFlag: string }
  $varuna: { endPoints: Record<string, string> }
  $message: (opts: Record<string, unknown>) => void
  $globalVar: { isYagiPrime: boolean }
}


export default /*#__PURE__*/_defineComponent({
  __name: 'YagiprimeFontPaymentDialog',
  setup(__props) {

/**
 * ヤギプライム・フォント課金ダイアログ（vue-final-modal）。
 * open は mitt（open-fontpayment-dialog / open-yagiprimefontpayment-dialog）。
 * 閉じた通知は mitt → hub forward（FormatEdit / MyPage の $on 互換）。
 */
const OPEN_FONTPAYMENT_DIALOG = 'open-fontpayment-dialog'
const OPEN_YAGIPRIME_FONTPAYMENT_DIALOG = 'open-yagiprimefontpayment-dialog'
const FONTPAYMENT_DIALOG_CLOSED = 'yagiprimefontpayment-dialog-closed'
const PAYMENT_YAGIPRIME_RESULT = 'payment_yagiprime_result'
const CLOSE_YAGIPRIME_FONTPAYMENT_DIALOG = 'close-yagiprimefontpayment-dialog'

const vm = getCurrentInstance()!.proxy as unknown as HostVm
const { user, getClient } = useAuth()

const visible = ref(false)
const formatid = ref('')
const permissionType = ref(1)
const fullscreenLoading = ref(false)
const paymentMethodTab = ref('card')
const isCampaign = ref(false)
const isCampaign4Me = ref(false)
const trialCampaignId = ref<number | null>(0)
const serviceName = ref('ヤギプライム')
const price = ref(550)
const isModalVisible = ref(false)

function currentUid(): string {
  return getClient()?.user?.uid ?? user.value?.uid ?? ''
}

function onVisibleInput(value: boolean) {
  if (value) {
    visible.value = true
    return
  }
  if (!visible.value) return
  closeFontpaymentDialog()
}

function openFontpaymentDialog(id?: unknown) {
  formatid.value = typeof id === 'string' ? id : ''
  visible.value = true
  isModalVisible.value = true
  permissionType.value = 1
  authStateChanged().catch(() => {})

  const formData = new FormData()
  formData.append('uuid', currentUid())
  formData.append('format_id', formatid.value)
  formData.append('dialog_name', 'yagiprimefontpayment_dialog')
  formData.append('permission_type', String(permissionType.value))
  formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
  formData.append('version', '2')
  formData.append('site_flag', 'r')
  formData.append('app_flag', vm.$site.appFlag)
  axios
    .post(vm.$varuna.endPoints.displayDialog, formData, {
      timeout: 60000
    })
    .catch(() => {})
}

function closeFontpaymentDialog() {
  if (!visible.value && !isModalVisible.value) return
  visible.value = false
  isModalVisible.value = false
  emitter.emit(FONTPAYMENT_DIALOG_CLOSED)
}

function handlePaymentResult(result: unknown) {
  fullscreenLoading.value = false
  if (result === 'error') {
    vm.$message({
      message: '決済失敗しました',
      type: 'error'
    })
  } else if (result === 'success' && isModalVisible.value) {
    vm.$message({
      message: '決済完了しました',
      type: 'success'
    })
    location.reload()
  }
}

function onPaymentYagiprimeResult(result: unknown) {
  if (result === 'success') {
    handlePaymentResult('success')
  } else if (result === 'error') {
    handlePaymentResult('error')
  }
}

async function authStateChanged() {
  const uid = currentUid()
  if (!uid) return
  try {
    const response = await axios.get(
      vm.$varuna.endPoints.isSubscribed + uid + '/',
      { timeout: 60000 }
    )
    if (response.data.is_subscribed) {
      vm.$message({
        message: 'すでにヤギプライム登録中です。',
        type: 'success'
      })
      visible.value = false
      isModalVisible.value = false
    }
  } catch {
    // ignore
  } finally {
    fullscreenLoading.value = false
  }

  const formData = new FormData()
  formData.append('uuid', uid)
  try {
    const response = await axios.post(
      vm.$varuna.endPoints.canUseTrialCampaign,
      formData,
      { timeout: 60000 }
    )
    if (response.data.can_use_trial_campaign) {
      isCampaign4Me.value = true
    } else {
      isCampaign4Me.value = false
      isCampaign.value = false
      trialCampaignId.value = null
    }
  } catch {
    // ignore
  } finally {
    fullscreenLoading.value = false
  }
}

function onLoadingOn() {
  fullscreenLoading.value = true
}

function onLoadingOff() {
  fullscreenLoading.value = false
}

useEventListener(window, 'popstate', closeFontpaymentDialog)

onMounted(() => {
  axios
    .get(vm.$varuna.endPoints.getTrialCampaign, { timeout: 60000 })
    .then((response) => {
      const row = response.data?.[0]
      if (!row) return
      const today = new Date()
      const startDate = new Date(row.trial_campaign_start_date)
      const endDate = new Date(row.trial_campaign_end_date)
      if (
        row.trial_campaign_is_active &&
        today >= startDate &&
        today <= endDate
      ) {
        isCampaign.value = true
        trialCampaignId.value = row.trial_campaign_id
      }
    })
    .catch(() => {})
    .finally(() => {
      fullscreenLoading.value = false
    })
})

onBeforeMount(() => {
  emitter.on(OPEN_YAGIPRIME_FONTPAYMENT_DIALOG, openFontpaymentDialog)
  emitter.on(OPEN_FONTPAYMENT_DIALOG, openFontpaymentDialog)
  emitter.on(CLOSE_YAGIPRIME_FONTPAYMENT_DIALOG, closeFontpaymentDialog)
  emitter.on('loading_on', onLoadingOn)
  emitter.on('loading_off', onLoadingOff)
  emitter.on(PAYMENT_YAGIPRIME_RESULT, onPaymentYagiprimeResult)
})

onBeforeUnmount(() => {
  emitter.off(OPEN_YAGIPRIME_FONTPAYMENT_DIALOG, openFontpaymentDialog)
  emitter.off(OPEN_FONTPAYMENT_DIALOG, openFontpaymentDialog)
  emitter.off(CLOSE_YAGIPRIME_FONTPAYMENT_DIALOG, closeFontpaymentDialog)
  emitter.off('loading_on', onLoadingOn)
  emitter.off('loading_off', onLoadingOff)
  emitter.off(PAYMENT_YAGIPRIME_RESULT, onPaymentYagiprimeResult)
})

return { __sfc: true,OPEN_FONTPAYMENT_DIALOG, OPEN_YAGIPRIME_FONTPAYMENT_DIALOG, FONTPAYMENT_DIALOG_CLOSED, PAYMENT_YAGIPRIME_RESULT, CLOSE_YAGIPRIME_FONTPAYMENT_DIALOG, vm, user, getClient, visible, formatid, permissionType, fullscreenLoading, paymentMethodTab, isCampaign, isCampaign4Me, trialCampaignId, serviceName, price, isModalVisible, currentUid, onVisibleInput, openFontpaymentDialog, closeFontpaymentDialog, handlePaymentResult, onPaymentYagiprimeResult, authStateChanged, onLoadingOn, onLoadingOff, PaymentForm }
}

})